/**
 * Appliance persist for the document source already saved in IndexedDB.
 *
 * POST /api/document-sources — store + SHA-256 readback only.
 * Import keeps working when this is skipped or rejected.
 */

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaders,
} from "@/services/cogo-platform-config";

export const DOCUMENT_SOURCE_STORE_SCHEMA_VERSION =
  "cogo.studio-document-source-store.v1";

/** Same extension map as report-upload-analysis inferContentType. */
export function inferDocumentSourceContentType(fileName: string): string {
  const normalized = fileName.toLowerCase();
  if (normalized.endsWith(".hwpx")) return "application/hwp+zip";
  if (normalized.endsWith(".hwp")) return "application/x-hwp";
  if (normalized.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (normalized.endsWith(".pptx")) {
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  }
  if (normalized.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (normalized.endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
}

export type PersistDocumentSourceArgs = {
  bytes: Uint8Array;
  fileName: string;
  projectId: string;
  contentType: string;
  clientSha256: string;
  uploadId?: string;
  signal?: AbortSignal;
};

export type PersistDocumentSourceResult =
  | {
      state: "success";
      storeAccepted: true;
      readbackAccepted: true;
      sha256: string;
      uploadId: string;
      projectId: string;
    }
  | { state: "skipped"; reason: "unauthenticated" }
  | { state: "rejected"; reason: string };

function rejected(reason: string): PersistDocumentSourceResult {
  return { state: "rejected", reason };
}

function errorReason(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const record = body as { error?: unknown; reason?: unknown; message?: unknown };
    if (typeof record.error === "string" && record.error.trim()) return record.error;
    if (typeof record.reason === "string" && record.reason.trim()) return record.reason;
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message;
    }
  }
  return fallback;
}

export async function persistDocumentSource(
  args: PersistDocumentSourceArgs,
): Promise<PersistDocumentSourceResult> {
  try {
    if (args.signal?.aborted) return rejected("aborted");
    const token = (await getStudioAccessToken())?.trim();
    if (!token) return { state: "skipped", reason: "unauthenticated" };
    if (args.signal?.aborted) return rejected("aborted");

    const contentType = args.contentType.trim() || inferDocumentSourceContentType(args.fileName);
    const query = new URLSearchParams({
      projectId: args.projectId,
      fileName: args.fileName,
      contentType,
      clientSha256: args.clientSha256,
    });
    if (args.uploadId) query.set("uploadId", args.uploadId);

    const headers = withCogoDevProxyHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/octet-stream",
    });

    const response = await fetch(
      cogoPlatformApiUrl(`/api/document-sources?${query.toString()}`),
      {
        method: "POST",
        headers,
        signal: args.signal,
        body: (() => {
          const copy = new Uint8Array(args.bytes.byteLength);
          copy.set(args.bytes);
          return copy.buffer as ArrayBuffer;
        })(),
      },
    );

    const body = await response.json().catch(() => null);
    if (!response.ok) {
      return rejected(errorReason(body, `http_${response.status}`));
    }

    if (
      !body ||
      typeof body !== "object" ||
      (body as { schemaVersion?: unknown }).schemaVersion !==
        DOCUMENT_SOURCE_STORE_SCHEMA_VERSION
    ) {
      return rejected("invalid_response");
    }

    const parsed = body as {
      storeAccepted?: unknown;
      readbackAccepted?: unknown;
      sha256?: unknown;
      uploadId?: unknown;
      projectId?: unknown;
    };
    const sha256 = typeof parsed.sha256 === "string" ? parsed.sha256 : "";
    const uploadId = typeof parsed.uploadId === "string" ? parsed.uploadId : "";
    const projectId = typeof parsed.projectId === "string" ? parsed.projectId : "";
    if (args.uploadId && uploadId !== args.uploadId) {
      return rejected("upload_identity_mismatch");
    }
    if (projectId !== args.projectId) {
      return rejected("project_identity_mismatch");
    }
    if (
      parsed.storeAccepted === true &&
      parsed.readbackAccepted === true &&
      sha256 === args.clientSha256
    ) {
      return {
        state: "success",
        storeAccepted: true,
        readbackAccepted: true,
        sha256,
        uploadId,
        projectId,
      };
    }

    if (sha256 && sha256 !== args.clientSha256) return rejected("hash_mismatch");
    return rejected("readback_not_accepted");
  } catch (error) {
    if (
      args.signal?.aborted ||
      (error &&
        typeof error === "object" &&
        "name" in error &&
        (error as { name?: unknown }).name === "AbortError")
    ) {
      return rejected("aborted");
    }
    return rejected(error instanceof Error && error.message ? error.message : "network");
  }
}
